import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, FileText, MessageCircle, Map as MapIcon, Compass, Lightbulb, ChevronLeft, ChevronRight, ArrowLeft, Bookmark, Sparkles, SlidersHorizontal, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  getCvPassport,
  getCvPublicProfile,
  type CvPassport,
  type CvPublicProfile,
} from '../../api/passport';
import { getRecruiterPreScreens, type PreScreenRecruiterResult } from '../../api/interview';
import { showToast } from '../../utils/errorHandler';
import ReachOutModal from '../CVSearch/ReachOutModal';
import StationsView from './StationsView';
import RealTalkView from './RealTalkView';
import MapView from './MapView';
import SkillsView from './SkillsView';
import PassportReactionBar from './PassportReactionBar';
import { RecruiterStatusView, RecruiterPreScreenView, type RecruiterPrefs } from './RecruiterPassportViews';

type PageKey = 'cv' | 'realtalk' | 'map' | 'stations' | 'skills' | 'status' | 'prescreen';
type Audience = 'explorer' | 'recruiter';

const PAGE_META: Record<PageKey, { label: string; tab: string; Icon: React.ComponentType<{ className?: string }> }> = {
  cv: { label: 'CV', tab: 'CV', Icon: FileText },
  stations: { label: 'Stations', tab: 'Stations', Icon: Compass },
  map: { label: 'Map', tab: 'Map', Icon: MapIcon },
  skills: { label: 'Skills', tab: 'Skills', Icon: Lightbulb },
  realtalk: { label: 'Real Talk', tab: 'Talk', Icon: MessageCircle },
  status: { label: 'Status', tab: 'Status', Icon: SlidersHorizontal },
  prescreen: { label: 'Intro', tab: 'Intro', Icon: ShieldCheck },
};

// Real Talk is explorer-only; Status + Pre-Screen are recruiter-only.
const EXPLORER_PAGES: PageKey[] = ['cv', 'stations', 'map', 'skills', 'realtalk'];
const RECRUITER_PAGES: PageKey[] = ['cv', 'stations', 'map', 'skills', 'status', 'prescreen'];

interface PassportViewerProps {
  cvId: string;
  /**
   * Who is viewing. 'explorer' (default) shows Real Talk; 'recruiter' swaps it
   * for Status + Pre-Screen.
   * NOTE: this is presentation only — real access control lives in the backend
   * (recruiter-gated endpoints). Never rely on this prop for security; only
   * pass data that came from a correspondingly gated source.
   */
  audience?: Audience;
  /**
   * Candidate status/preferences for the recruiter Status tab. Must originate
   * from a recruiter-gated source (e.g. Spotlight / candidate feed).
   */
  recruiterPrefs?: RecruiterPrefs | null;
  /**
   * Stable candidate (profile) id, for the recruiter Pre-Screen tab. Used to
   * fetch the candidate's shared pre-screen results from the recruiter-gated
   * endpoint. Only meaningful when audience === 'recruiter'.
   */
  candidateId?: string | null;
  /** Optional candidate prev/next slot (browse between candidates). */
  navSlot?: React.ReactNode;
  /** Page to open on first render (defaults to CV). */
  initialPage?: PageKey;
  /** Override fetched passport/realtalk (e.g. preview mock). */
  passportOverride?: CvPassport | null;
  realTalkOverride?: CvPublicProfile | null;
  /** Reuse already-loaded CV images (e.g. from Explore) to skip a refetch. */
  imagesOverride?: string[] | null;
  onContact?: () => void;
  /** Explore affordances (optional). */
  onBack?: () => void;
  isSaved?: boolean;
  onToggleSave?: (cvId: string, e: React.MouseEvent) => void;
  /**
   * Show the explorer→explorer "vibe" reaction bar (Slay / Let's talk / …).
   * Only meaningful for the explorer audience on someone else's passport.
   */
  enableReactions?: boolean;
  /** Hide "Spark a conversation" (e.g. when passport is opened from an existing chat). */
  showContact?: boolean;
  /** @deprecated no longer shown in UI; kept for caller compatibility. */
  score?: number;
  className?: string;
  /**
   * When true (default), fill the parent height and scroll the page body inside
   * (modals / fixed shells). When false, grow with content like MyPassport edit
   * so a full A4 CV page can show without an inner scroll box.
   */
  fillHeight?: boolean;
}

const PassportViewer: React.FC<PassportViewerProps> = ({
  cvId,
  audience = 'explorer',
  recruiterPrefs,
  candidateId,
  navSlot,
  initialPage = 'cv',
  passportOverride,
  realTalkOverride,
  imagesOverride,
  onContact,
  onBack,
  isSaved = false,
  onToggleSave,
  enableReactions = false,
  showContact = true,
  className = '',
  fillHeight = true,
}) => {
  const { apiCall } = useAuth();
  const usingOverride = passportOverride !== undefined;

  // Recruiter-only: fetch upfront whether this candidate shared an Intro, so the
  // Intro tab can move to the front and open first.
  const [preScreenResults, setPreScreenResults] = useState<PreScreenRecruiterResult[] | null>(null);
  useEffect(() => {
    if (audience !== 'recruiter' || !candidateId) {
      setPreScreenResults(null);
      return;
    }
    let cancelled = false;
    getRecruiterPreScreens(apiCall, candidateId)
      .then((r) => { if (!cancelled) setPreScreenResults(r.prescreens || []); })
      .catch(() => { if (!cancelled) setPreScreenResults([]); });
    return () => { cancelled = true; };
  }, [audience, candidateId, apiCall]);

  const hasIntro = audience === 'recruiter' && !!preScreenResults && preScreenResults.length > 0;

  const pages = useMemo<PageKey[]>(() => {
    if (audience === 'recruiter') {
      return hasIntro
        ? (['prescreen', ...RECRUITER_PAGES.filter((p) => p !== 'prescreen')] as PageKey[])
        : RECRUITER_PAGES;
    }
    return EXPLORER_PAGES;
  }, [audience, hasIntro]);

  const [passport, setPassport] = useState<CvPassport | null>(passportOverride ?? null);
  const [realTalk, setRealTalk] = useState<CvPublicProfile | null>(realTalkOverride ?? null);
  const [images, setImages] = useState<string[] | null>(imagesOverride ?? null);
  const [loading, setLoading] = useState<boolean>(!usingOverride);
  const [error, setError] = useState<string | null>(null);

  // Track the current page by key (not index) so reordering the tabs (Intro to
  // the front) never changes which page is shown.
  const [pageKey, setPageKey] = useState<PageKey>(initialPage);
  const userNavigatedRef = useRef(false);
  const pageIdx = Math.max(0, pages.indexOf(pageKey));
  const [dir, setDir] = useState<1 | -1>(1);
  const [reachOutOpen, setReachOutOpen] = useState(false);

  // When we learn (recruiter) that an Intro exists, open it first — unless the
  // recruiter already navigated to another tab.
  useEffect(() => {
    if (hasIntro && !userNavigatedRef.current) {
      setPageKey('prescreen');
    }
  }, [hasIntro]);

  const handleContact = useCallback(() => {
    if (onContact) onContact();
    else setReachOutOpen(true);
  }, [onContact]);

  // CV images: reuse passed-in images when available, else fetch the existing
  // public endpoint (no track_view here).
  useEffect(() => {
    if (imagesOverride && imagesOverride.length) {
      setImages(imagesOverride);
      return;
    }
    let cancelled = false;
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    apiCall(`${apiBase}/api/v1/cv/${cvId}/images`)
      .then(async (res) => {
        if (cancelled || !res.ok) return;
        const data = await res.json();
        if (!cancelled) setImages(data?.images || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cvId, apiCall, imagesOverride]);

  // Passport JSON + Real Talk.
  useEffect(() => {
    if (usingOverride) {
      setPassport(passportOverride ?? null);
      setRealTalk(realTalkOverride ?? null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Real Talk is explorer-only — don't even fetch it for recruiters.
    Promise.all([
      getCvPassport(apiCall, cvId),
      audience === 'recruiter'
        ? Promise.resolve(null)
        : getCvPublicProfile(apiCall, cvId).catch(() => null),
    ])
      .then(([p, rt]) => {
        if (cancelled) return;
        if (!p) setError('No CV content found.');
        setPassport(p);
        setRealTalk(rt);
      })
      .catch((e) => !cancelled && setError(e?.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [cvId, apiCall, usingOverride, passportOverride, realTalkOverride, audience]);

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(pages.length - 1, idx));
    setDir(clamped >= pageIdx ? 1 : -1);
    userNavigatedRef.current = true;
    setPageKey(pages[clamped]);
  };

  const page = pages[pageIdx];

  const pageContent = useMemo(() => {
    if (page === 'cv') {
      if (!images) {
        return (
          <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading CV…
          </div>
        );
      }
      if (images.length === 0) {
        return (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <div className="text-center"><div className="text-2xl mb-1.5">📄</div><p className="text-xs">No CV pages</p></div>
          </div>
        );
      }
      return (
        <div className="max-w-2xl mx-auto px-3 py-4 space-y-3">
          {images.map((img, i) => (
            <img
              key={i}
              src={`data:image/png;base64,${img}`}
              alt={`CV page ${i + 1}`}
              className="w-full rounded-lg border border-slate-200 shadow-sm"
            />
          ))}
        </div>
      );
    }
    if (page === 'realtalk') {
      return (
        <RealTalkView
          sentence={realTalk?.real_talk_sentence ?? null}
          tags={realTalk?.real_talk_tags ?? null}
          isContactable={realTalk?.is_contactable ?? false}
          onContact={handleContact}
        />
      );
    }
    if (page === 'map') {
      return <MapView locations={passport?.locations ?? []} stations={passport?.stations ?? []} />;
    }
    if (page === 'skills') {
      return (
        <SkillsView
          skills={passport?.skills ?? []}
          languages={passport?.languages ?? []}
          interests={passport?.interests ?? []}
          highlights={passport?.highlights ?? []}
        />
      );
    }
    if (page === 'status') {
      return <RecruiterStatusView prefs={recruiterPrefs} />;
    }
    if (page === 'prescreen') {
      return <RecruiterPreScreenView candidateId={candidateId} results={preScreenResults} />;
    }
    return <StationsView stations={passport?.stations ?? []} />;
  }, [page, images, realTalk, passport, handleContact, recruiterPrefs, candidateId, preScreenResults]);

  // Contact (Spark a conversation) is the explorer→explorer flow; recruiters
  // reach out through their own pipeline, so never auto-show it for recruiters.
  const canContact = showContact && audience === 'explorer' && !!realTalk?.is_contactable;
  const showReactions = enableReactions && audience === 'explorer';
  const showHeader = !!onBack || !!onToggleSave || canContact;

  return (
    <div className={`flex flex-col ${fillHeight ? 'h-full' : ''} ${className}`}>
      {navSlot}

      {showHeader && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-white">
          {onBack ? (
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-pink-500 px-2 py-1 rounded-lg hover:bg-slate-50"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : <span />}

          <div className="flex items-center gap-2">
            {canContact && (
              <button
                onClick={handleContact}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-sm transition-all"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Spark a conversation</span>
                <span className="sm:hidden">Chat</span>
              </button>
            )}
            {onToggleSave && (
              <button
                onClick={(e) => onToggleSave(cvId, e)}
                className={`p-1.5 rounded-lg transition-colors ${isSaved ? 'text-pink-500' : 'text-slate-400 hover:text-pink-500 hover:bg-slate-50'}`}
                title={isSaved ? 'Remove from saved' : 'Save CV'}
              >
                <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reaction strip (explorer → explorer, free) */}
      {showReactions && (
        <div className="px-3 py-2 border-b border-slate-100 bg-gradient-to-r from-pink-50/60 to-rose-50/40">
          <div className="max-w-2xl mx-auto flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap pr-0.5">React</span>
            <PassportReactionBar cvId={cvId} apiCall={apiCall} loadInitial />
          </div>
        </div>
      )}

      {/* Page tabs */}
      <div className={`${fillHeight ? 'sticky top-0 z-10' : ''} bg-white/90 backdrop-blur border-b border-slate-100`}>
        <div className="max-w-2xl mx-auto px-3 flex">
          {pages.map((key, i) => {
            const active = i === pageIdx;
            const { Icon, tab } = PAGE_META[key];
            return (
              <button
                key={key}
                onClick={() => goTo(i)}
                className={`relative flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-2.5 text-[11px] sm:text-[13px] font-semibold whitespace-nowrap transition-colors ${
                  active ? 'text-pink-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{tab}</span>
                {active && (
                  <motion.div
                    layoutId="passportTab"
                    className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full bg-gradient-to-r from-pink-400 to-rose-500"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Swipeable page area — fill+scroll in shells; grow with content in Explore */}
      <div className={`relative ${fillHeight ? 'flex-1 overflow-hidden min-h-0' : ''}`}>
        {loading && page !== 'cv' ? (
          <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating…
          </div>
        ) : error && page !== 'cv' ? (
          <div className="px-4 py-10 text-center text-rose-500 text-sm">{error}</div>
        ) : (
          <AnimatePresence initial={false} custom={dir} mode="wait">
            <motion.div
              key={page}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.22 }}
              drag="x"
              dragDirectionLock
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.18}
              onDragEnd={(_, info) => {
                if (info.offset.x < -60) goTo(pageIdx + 1);
                else if (info.offset.x > 60) goTo(pageIdx - 1);
              }}
              // Let the browser own vertical scrolling natively; Framer only
              // handles horizontal swipes. Without this the drag listener
              // swallows the first touch and inner scroll feels unresponsive.
              style={{ touchAction: 'pan-y' }}
              className={fillHeight ? 'h-full overflow-y-auto' : ''}
            >
              {pageContent}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Desktop arrows for page nav */}
        {pageIdx > 0 && (
          <button
            onClick={() => goTo(pageIdx - 1)}
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 border border-slate-200 shadow-sm text-slate-500 hover:text-pink-500"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        {pageIdx < pages.length - 1 && (
          <button
            onClick={() => goTo(pageIdx + 1)}
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 border border-slate-200 shadow-sm text-slate-500 hover:text-pink-500"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Page dots */}
      <div className="flex items-center justify-center gap-1.5 py-2 border-t border-slate-100">
        {pages.map((key, i) => (
          <button
            key={key}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all ${i === pageIdx ? 'w-5 bg-pink-500' : 'w-1.5 bg-slate-300'}`}
            aria-label={PAGE_META[key].label}
          />
        ))}
      </div>

      <ReachOutModal
        isOpen={reachOutOpen}
        onClose={() => setReachOutOpen(false)}
        cvId={cvId}
        onSubmitSuccess={() => showToast('Message sent', 'success')}
        apiCall={apiCall}
      />
    </div>
  );
};

export default PassportViewer;
